import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import { ProgressKey } from 'assets/components/progressWindow/ProgressWindow';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import iterateWithProgress, { ProgressInfo } from 'assets/utils/data/Iterations';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { autoTriggerDownload } from 'assets/utils/dom/Download';
import { generateDataUrl } from 'assets/utils/dom/UrlParsing';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import {
  getProductDocumentsFinalFields,
  getProductEnhancementsDocumentsFinalFields,
} from 'assets/utils/other/Utils.Other.GetDocumentFields';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { compact, first, flatten, isEmpty, keyBy, keys, mapValues, uniqBy } from 'lodash';
import User from 'models/core/user/Model.User';
import ContractConsentRequestType from 'models/enums/ContractConsentRequestType';
import { ContractSigningTypes } from 'models/enums/ContractSigningTypes';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import DocuSignEnvelopeStatuses from 'models/enums/DocuSignEnvelopeStatuses';
import ContractRefund from 'models/sales/contractRefund/Model.ContractRefund';
import { documentTranslationListApi } from 'models/localization/documentTranslation/Model.DocumentTranslation';
import Product from 'models/productManagement/product/Model.Product';
import { productEnhancementListApi } from 'models/productManagement/productEnhancement/Model.ProductEnhancement';
import RefundConfigTemplate from 'models/productManagement/refundConfigTemplate/Model.RefundConfigTemplate';
import Subfee from 'models/productManagement/subfee/Model.Subfee';
import contractAddDocumentToVertaforePolicyApi from 'models/sales/contract/addDocumentToVertaforePolicy/Api.AddDocumentToVertaforePolicy';
import contractChangeCreatorApi from 'models/sales/contract/changeCreator/Api.Contract.ChangeCreator';
import createStatementsFromContractListApi from 'models/sales/contract/createStatementsFromContractList/Api.createStatementsFromContractList';
import contractCreateVertaforePolicyApi from 'models/sales/contract/createVertaforePolicy/Api.CreateVertaforePolicy';
import contractExportApi from 'models/sales/contract/export/Api.Contract.Export';
import contractGenerateDocumentsApi from 'models/sales/contract/generateDocuments/Api.Contract.GenerateDocuments';
import revisionGenerateDocumentsApi from 'models/sales/contract/generateRevisionDocuments/Api.Contract.GenerateRevisionDocuments';
import contractGetContractsForDocuSignApi from 'models/sales/contract/getContractsForDocuSign/Api.Contract.GetContractsForDocuSign';
import contractGetEnvelopeApi from 'models/sales/contract/getEnvelope/Api.Contract.GetEnvelope';
import contractListApi from 'models/sales/contract/list/Api.Contract.List';
import Contract from 'models/sales/contract/Model.Contract';
import contractRevisionsApi from 'models/sales/contract/revisions/Api.Contract.Revisions';
import contractSendConsentApi from 'models/sales/contract/sendConsent/Api.Contract.SendConsent';
import contractSendDocumentForSigningApi from 'models/sales/contract/sendDocumentForSigning/Api.Contract.SendDocumentForSigning';
import contractStatusFlowApi from 'models/sales/contract/statusFlow/Api.Contract.StatusFlow';
import contractUploadSignedDocumentApi from 'models/sales/contract/uploadSignedDocument/Api.Contract.UploadSignedDocument';
import ContractCancellation from 'models/sales/contractCancellation/Model.ContractCancellation';
import ContractComment from 'models/sales/contractComment/ContractComment';
import contractCommentDeleteApi from 'models/sales/contractComment/delete/Api.ContractComment.Delete';
import contractCommentSaveApi from 'models/sales/contractComment/save/Api.ContractComment.Save';
import ContractRevision from 'models/sales/contractRevision/Model.ContractRevision';
import ContractStatusAuditLog from 'models/sales/contractStatusAuditLog/Model.ContractStatusAuditLog';
import Lender from 'models/sales/lender/Model.Lender';
import { useEffect, useMemo, useState } from 'react';
import { salesContractIndexRoute } from './Sales.Contract.Index';

export enum SalesContractAction {
  INFO = 'info',
  ADJUST_DISTRIBUTIONS = 'adjustDistributions',
  CHANGE_STATUS = 'changeStatus',
  TRANSFER = 'transfer',
  REFUND = 'refund',
}
export enum SalesContractInfoTab {
  INFO = 'info',
  DOCUMENTS = 'documents',
  REVISIONS = 'revisions',
  CANCELLATION = 'cancellation',
  NOTES = 'notes',
  STATUS_AUDIT_LOGS = 'statusAuditLogs',
  CLIENT_HISTORY = 'clientHistory',
  ALERTS = 'alerts',
}
export enum SalesContractVertaforeSyncType {
  SYNC_CONTRACTS = 'syncContracts',
  SYNC_CONTRACT_DOCUMENTS = 'syncDocuments',
}
export function useSalesContractRouter() {
  return usePageRouter<Module.Sales.Contract.Params, Module.Sales.Contract.Query>({
    route: salesContractIndexRoute,
  });
}
export default function useSalesContract() {
  const { permissions, user } = useAuthStore();
  const pageRouter = useSalesContractRouter();
  const { lang } = getLocales();
  const [isStatusChanging, setIsStatusChanging] = useState<boolean>(false);
  const [selectedContracts, setSelectedContracts] = useState<Model.IContract[]>([]);

  const contractStatusInfoApi = useApi({ action: contractStatusFlowApi });
  const contractStatusInfo = contractStatusInfoApi.payload;

  const canView =
    permissions.CREATE_CONTRACT ||
    permissions.EDIT_CONTRACT ||
    permissions.DELETE_CONTRACT ||
    permissions.CHANGE_CONTRACT_CREATOR ||
    permissions.EDIT_CONTRACT_UNRESTRICTED ||
    permissions.UPDATE_CONTRACT_STATUS ||
    permissions.UPDATE_CONTRACT_STATUS_UNRESTRICTED ||
    permissions.TRANSFER_CONTRACT ||
    permissions.VERTAFORE_SYNC_CONTRACT ||
    permissions.UPLOAD_DOCUMENTS_CONTRACT ||
    permissions.SEND_CONTRACT_FOR_SIGNING ||
    permissions.REUPLOAD_SIGNED_CONTRACTDOCUMENT ||
    permissions.EDIT_CONTRACTCOMMENT ||
    permissions.DELETE_CONTRACTCOMMENT ||
    permissions.VIEW_CONTRACTCOMMENT ||
    permissions.VIEW_CONTRACT_LOGS ||
    permissions.CREATE_STATEMENTS_FROM_CONTRACT_LIST ||
    permissions.RESTRICT_EDIT_EFFECTIVEDATE_CONTRACT;

  const listApi = useApi(
    {
      action: Contract.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        dealerId: user?.dealers?.map((d) => d.id) ?? [],
        distributorId: user.distributorId ?? undefined,
      },
      body: pageRouter.query.list,
      wait: !canView,
      callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    },
    [JSON.stringify(pageRouter.query.list)]
  );
  const findApi = useApi(
    {
      action: Contract.find,
      wait: !canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );
  const exportApi = useApi({
    action: Contract.export,
    wait: !canView || isEmpty(listApi?.payload?.data),
  });

  const selected = findApi?.payload;

  const productApi = useApi({
    action: Product.find,
    wait: true,
  });
  useEffect(() => {
    if (selected?.productId) productApi.execute({ id: selected.productId });
    else productApi.reset(true);
  }, [selected?.productId]);

  const lenderApi = useApi({
    action: Lender.find,
    wait: true,
  });
  useEffect(() => {
    if (selected?.vehicle?.lenderId) lenderApi.execute({ id: selected.vehicle?.lenderId });
    else lenderApi.reset(true);
  }, [selected?.vehicle?.lenderId]);
  useEffect(() => {
    if (lenderApi?.payload && lenderApi?.payload?.id === selected?.vehicle?.lenderId)
      selected.vehicle.lender = new Lender(lenderApi?.payload);
  }, [lenderApi?.payload]);

  //#region Subfees
  const subfeeApi = useApi({
    action: Subfee.list,
    wait: true,
  });

  const subfeeIds = useMemo(() => selected?.subfees?.map((it) => Number(it.subfeeId)) ?? [], [selected?.subfees]);

  useEffect(() => {
    if (subfeeIds.length) {
      subfeeApi.execute({
        id: subfeeIds,
      });
    } else {
      subfeeApi.reset(true);
    }
  }, [subfeeIds]);

  const subfees = useMemo(() => subfeeApi?.payload?.data, [subfeeApi?.payload?.data]);

  const subfeeNameMap = useMemo(() => {
    return mapValues(
      keyBy(subfees ?? [], (s) => s.id.toString()),
      (s) => s.name
    );
  }, [subfees]);

  const originalSubfeeDocs = useMemo(() => flatten(subfees?.map((it) => it.documents ?? []) ?? []), [subfees]);
  const originalSubfeeDocIds = useMemo(() => originalSubfeeDocs.map((it) => it.id), [originalSubfeeDocs]);

  const subfeeDocumentTranslationApi = useApi({
    action: documentTranslationListApi,
    wait: true,
  });
  useEffect(() => {
    if (originalSubfeeDocIds.length) {
      subfeeDocumentTranslationApi.execute({
        documentType: 'MediaFileSubfee',
        documentId: originalSubfeeDocIds,
      });
    } else {
      subfeeDocumentTranslationApi.reset(true);
    }
  }, [originalSubfeeDocIds]);

  const subfeeDocs = useMemo(() => {
    const translatedSubfeeDocs = subfeeDocumentTranslationApi?.payload?.data ?? [];

    return originalSubfeeDocs.map((doc) => {
      const translatedSubfeeDoc = translatedSubfeeDocs.find(
        (t) =>
          t.documentId === doc.id &&
          t.documentType === 'MediaFileSubfee' &&
          t.languageIsoCode === selected?.documentLanguageIsoCode
      );

      return translatedSubfeeDoc ?? doc;
    });
  }, [originalSubfeeDocs, subfeeDocumentTranslationApi?.payload?.data, selected?.documentLanguageIsoCode]);
  //#endregion

  const statuses = useMemo(() => {
    return keys(contractStatusInfo).map((status) => {
      const [primary, secondary, reason] = (status ? lang[status] : status).split('_');
      const mainStatus = combineStrings(' - ', primary, secondary);
      return {
        id: status,
        subtitle: mainStatus,
        title: reason ?? mainStatus,
      };
    });
  }, [contractStatusInfo]);

  //#region Revisions
  const revisionsApi = useApi({ action: contractRevisionsApi, wait: true });
  useEffect(() => {
    if (selected?.id) {
      revisionsApi.execute({ id: selected?.id });
    } else {
      revisionsApi.reset(true);
    }
  }, [selected?.id]);

  const [selectedRevision, selectRevision] = useState<ContractRevision>();
  const revisions = useMemo(() => {
    return revisionsApi.payload?.map((data) => {
      return new ContractRevision({
        ...data,
        contractJson: { ...data.contractJson, product: selected?.product?.toJSON() },
      });
    });
  }, [revisionsApi.payload]);
  const { payload: revisionUsersPayload } = useApi(
    { action: User.list, body: { id: revisions?.map((it) => it.userId) }, wait: !revisions },
    [revisions]
  );

  //#endregion

  //#region ContractComments
  const hasAnyContractCommentPermission =
    permissions?.VIEW_CONTRACTCOMMENT || permissions?.EDIT_CONTRACTCOMMENT || permissions?.DELETE_CONTRACTCOMMENT;
  const commentsApi = useApi({
    action: ContractComment.list,

    wait: true,
  });
  useEffect(() => {
    if (selected?.id) {
      commentsApi.execute({ contractId: selected?.id });
    } else {
      commentsApi.reset(true);
    }
  }, [selected?.id]);
  //#endregion

  //#region ContractStatusAuditLogs
  const contractStatusAuditLogListApi = useApi({
    action: ContractStatusAuditLog.list,
    wait: true,
  });
  useEffect(() => {
    if (selected?.id) {
      contractStatusAuditLogListApi.execute({ contractId: [selected?.id] });
    } else {
      contractStatusAuditLogListApi.reset(true);
    }
  }, [selected?.id]);
  //#endregion

  //#region ClientHistoryLogs
  const contractCancellationListApi = useApi({
    action: ContractCancellation.list,
    wait: true,
  });
  useEffect(() => {
    if (selected?.id) {
      contractCancellationListApi.execute({ contractId: [selected?.id] });
    } else {
      contractCancellationListApi.reset(true);
    }
  }, [selected?.id]);
  const contractCancellation = first(contractCancellationListApi?.payload?.data);

  const contractRefundListApi = useApi({
    action: ContractRefund.find,
    wait: true,
  });
  useEffect(() => {
    if (contractCancellation?.contractRefund?.id) {
      contractRefundListApi.execute({ id: contractCancellation.contractRefund.id });
    } else {
      contractRefundListApi.reset(true);
    }
  }, [contractCancellation?.contractRefund?.id]);
  const contractRefund = contractRefundListApi?.payload;
  //#endregion

  const userListApi = useApi({
    action: User.list,
    body: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
    wait: !canView || !permissions.CHANGE_CONTRACT_CREATOR,
  });

  async function exportContractsToExcel() {
    const contractExportQuery = { ...pageRouter.query.list };
    delete contractExportQuery.limit;
    delete contractExportQuery.offset;

    const res = await contractExportApi({ ...contractExportQuery });

    if (res?.status === HttpStatus.ok) {
      autoTriggerDownload({
        url: generateDataUrl(MimeType.appOPEN_XML_SHEET, res?.payload),
        name: `Contracts_${date().format('DD-MM-YY')}.xlsx`,
      });
      addLog({ success: lang.exportSuccess });
    } else addLog({ error: lang.exportError });
  }

  async function changeContractCreator({ contractId, creatorId }: { contractId: number; creatorId: number }) {
    const res = await contractChangeCreatorApi({ contractId, creatorId });

    if (res?.status === HttpStatus.ok) {
      addLog({ success: lang.contractCreatorSuccessfullyChanged });
      listApi.execute((body) => body);
    } else addLog({ error: res.message });
  }

  function isContractEligibleForSelection(contract: Model.IContract) {
    return permissions.CREATE_STATEMENTS_FROM_CONTRACT_LIST && !contract.statementId;
  }

  async function getAllSelectedContracts() {
    const newQuery = { ...pageRouter?.query?.list };
    delete newQuery.limit;
    delete newQuery.offset;
    const {
      payload: { data: filteredContracts },
    } = await contractListApi({ ...newQuery });

    const selectableContracts = filteredContracts?.filter(isContractEligibleForSelection);

    return selectedContracts?.length === selectableContracts?.length ? [] : selectableContracts?.map((it) => it);
  }

  //#region Veraforte
  async function getSyncedLimitCount<T>(
    contractsToSync: Model.IContract[],
    onSaveProgress: (key: ProgressKey, progressInfo: ProgressInfo) => void,
    onSync: ({ contractId }: { contractId: number }) => Promise<Api.Res<T>>
  ) {
    return await iterateWithProgress(
      contractsToSync,
      async (contract) => {
        if (contract?.id) {
          await onSync({ contractId: contract.id });
        }
      },
      (p) => onSaveProgress('syncAllWithVertafore', p)
    );
  }
  const contractUnknownVertaforeStatusListApi = useApi({ action: Contract.getContractsWithUnknownVertaForeStatus });
  async function syncContractWithVertafore({ contractId }: { contractId: number }) {
    const res = await contractCreateVertaforePolicyApi({ contractId });

    if (res?.status === HttpStatus.ok) {
      addLog({ success: lang.successfullySyncedWithVertafore });
      findApi.execute((body) => body);
    } else addLog({ error: res.message });
  }
  const contractNotUploadedVertaforeStatusListApi = useApi({
    action: Contract.getContractsWithNotUploadedVertaForeStatus,
  });
  async function syncContractDocumentWithVertafore({ contractId }: { contractId: number }) {
    const res = await contractAddDocumentToVertaforePolicyApi({ contractId });

    if (res?.status === HttpStatus.ok && res.payload) {
      addLog({ success: `${lang.contractDocument} ${lang.successfullySyncedWithVertafore}` });
      findApi.execute((body) => body);
    } else
      addLog({
        error:
          res.message ??
          `${lang.contractDocument} ${replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, {
            status: 'FAILED',
          })} `,
      });
  }

  async function syncAllWithVertafore(
    onSaveProgress: (key: ProgressKey, progressInfo: ProgressInfo) => void,
    syncType: SalesContractVertaforeSyncType.SYNC_CONTRACTS | SalesContractVertaforeSyncType.SYNC_CONTRACT_DOCUMENTS
  ) {
    const contracts = await contractListApi({
      statusCode: [
        ContractStatusCode.OPEN_SUBMITTED,
        ContractStatusCode.OPEN_DEFERRED,
        ContractStatusCode.OPEN_UNDER_REVIEW,
        ContractStatusCode.OPEN_UNPAID,
        ContractStatusCode.OPEN_WAITING_CONSENT,
        ContractStatusCode.PAID_ACTIVE,
        ContractStatusCode.PAID_EXPIRED,
      ],
    });

    let syncedLimitCount = 0;
    let contractsToSync: Model.IContract[] = [];
    if (syncType === SalesContractVertaforeSyncType.SYNC_CONTRACTS) {
      contractsToSync = contractUnknownVertaforeStatusListApi?.payload
        ?.map((cid) => contracts?.payload?.data?.find((it) => it.id === cid))
        ?.filter(Boolean);

      if (isEmpty(contractsToSync)) return addLog({ warning: 'No contracts for sync!' });

      syncedLimitCount = await getSyncedLimitCount(contractsToSync, onSaveProgress, contractCreateVertaforePolicyApi);
    } else if (syncType === SalesContractVertaforeSyncType.SYNC_CONTRACT_DOCUMENTS) {
      contractsToSync = contractNotUploadedVertaforeStatusListApi?.payload
        ?.map((cid) => contracts?.payload?.data?.find((it) => it.id === cid))
        ?.filter(Boolean);

      if (isEmpty(contractsToSync)) return addLog({ warning: 'No contract documents for sync!' });

      syncedLimitCount = await getSyncedLimitCount(
        contractsToSync,
        onSaveProgress,
        contractAddDocumentToVertaforePolicyApi
      );
    }

    if (syncedLimitCount && syncedLimitCount > 0) listApi.execute((b) => b);
  }
  //#endregion

  const productEnhancementList = useApi({
    action: productEnhancementListApi,
  });
  const enhancements = productEnhancementList.payload?.data ?? [];
  const enhancementDocs = uniqBy(
    flatten(
      compact(selected?.contractEnhancements)?.map(
        (ce) => enhancements.find((e) => e?.id === ce?.productEnhancementId)?.productEnhancementDocuments
      )
    ),
    (it) => it?.id
  );

  //#region Contract documents
  async function regenerateContractDocuments(selectedContract: Model.IContract, languageIsoCode?: Locale.Languages) {
    const productDocuments = productApi.payload?.productDocuments ?? [];
    const additionalFields = flatten(
      productDocuments?.map((d) =>
        d.fillablePdfFields
          ?.filter((a) => a.defaultValue)
          ?.map((a) => ({
            productDocumentId: d.id,
            key: a.key,
            value: a.defaultValue,
          }))
      )
    );
    const enhancementAdditionalFields = flatten(
      enhancementDocs?.map((d) =>
        d.productEnhancementAdditionalFields
          ?.filter((a) => a.defaultValue)
          ?.map((a) => ({
            productEnhancementDocumentId: d.id,
            key: a.key,
            value: a.defaultValue,
          }))
      )
    );

    const finalFields = getProductDocumentsFinalFields(selectedContract, productDocuments, additionalFields);
    const finalEnhancementFields = getProductEnhancementsDocumentsFinalFields(
      selectedContract,
      enhancementDocs,
      enhancementAdditionalFields
    );

    if (finalFields?.length) {
      const res = selectedContract?.revisionNumber
        ? await revisionGenerateDocumentsApi({
            contractId: selectedContract?.id,
            productPdfInputItems: finalFields,
            productEnhancementPdfInputItems: finalEnhancementFields,
            languageIsoCode,
          })
        : await contractGenerateDocumentsApi({
            contractId: selectedContract?.id,
            productPdfInputItems: finalFields,
            productEnhancementPdfInputItems: finalEnhancementFields,
            languageIsoCode,
          });
      if (res?.status !== HttpStatus.ok) {
        addLog({ error: res.message });
      } else {
        addLog({ success: lang.saveSuccess });
      }
      findApi.execute((b) => b);
    }
  }
  //#endregion

  //#region DocuSign
  const [isFetchingEnvelopes, setIsFetchingEnvelopes] = useState<boolean>(false);
  async function getEnvelopes({
    contractQuery,
    singingType,
    envelopeStatus,
  }: {
    contractQuery: Api.Core.Contract.GetContractsForDocuSign.IRequest;
    singingType: Model.Enum.ContractSigningTypes;
    envelopeStatus: Model.Enum.DocuSignEnvelopeStatuses;
  }) {
    delete contractQuery.limit;
    delete contractQuery.offset;
    delete contractQuery.isSentForSigning;
    delete contractQuery.isSigned;
    delete contractQuery.isSignedWithDocuSign;
    delete contractQuery.isSignedManually;

    setIsFetchingEnvelopes(true);
    const res = await contractGetContractsForDocuSignApi({ singingType }, contractQuery);

    if (res.status !== HttpStatus.ok) {
      addLog({ error: res.message });
      setIsFetchingEnvelopes(false);
    }

    return await Promise.all(
      res.payload?.data?.map(async (c) => {
        if (c) {
          const { payload: envelope, status } = await contractGetEnvelopeApi({
            envelopeId: c.docuSignEnvelopeId,
          });
          if (status === HttpStatus.ok && envelope && envelope.envelopeStatus === envelopeStatus) {
            return {
              ...envelope,
              contract: c,
            };
          }
        }
      })
    );
  }
  async function resendAllForSigning(onSaveProgress: (key: ProgressKey, progressInfo: ProgressInfo) => void) {
    const contractQuery = { ...pageRouter.query.list };
    const envelopes = await getEnvelopes({
      contractQuery,
      singingType: ContractSigningTypes.DocuSign,
      envelopeStatus: DocuSignEnvelopeStatuses.Voided,
    });
    if (isEmpty(envelopes)) {
      setIsFetchingEnvelopes(false);
      return addLog({ warning: 'No contracts for re-signing!' });
    }

    const limitCount = await iterateWithProgress(
      envelopes,
      async (envelope) => {
        if (envelope && envelope.contract) {
          await contractSendDocumentForSigningApi({ contractId: envelope.contract.id });
        }
      },
      (p) => onSaveProgress('resendForSigning', p)
    );

    setIsFetchingEnvelopes(false);
    if (limitCount && limitCount > 0) listApi.execute((b) => b);
  }
  async function reuploadAllSignedDocuments(onSaveProgress: (key: ProgressKey, progressInfo: ProgressInfo) => void) {
    const contractQuery = { ...pageRouter.query.list };
    const envelopes = await getEnvelopes({
      contractQuery,
      singingType: ContractSigningTypes.DocuSign,
      envelopeStatus: DocuSignEnvelopeStatuses.Completed,
    });

    if (isEmpty(envelopes)) {
      setIsFetchingEnvelopes(false);
      return addLog({ warning: 'No signed contracts for signed document reupload!' });
    }

    const limitCount = await iterateWithProgress(
      envelopes,
      async (envelope) => {
        if (envelope && envelope.contract) {
          await contractUploadSignedDocumentApi({ envelopeId: envelope.envelopeId });
        }
      },
      (p) => onSaveProgress('reuploadAllSignedDocumentsFromDocuSign', p)
    );

    setIsFetchingEnvelopes(false);
    if (limitCount && limitCount > 0) listApi.execute((b) => b);
  }
  //#endregion

  //#region Statements
  const hasMixedDealers = useMemo(() => {
    if (!selectedContracts?.length) return false;
    const ids = new Set<number>();
    for (const c of selectedContracts) {
      if (typeof c?.dealerId === 'number') ids.add(c.dealerId);
    }
    return ids.size > 1;
  }, [selectedContracts]);

  async function createStatementFromSelectedContracts() {
    if (!permissions?.CREATE_STATEMENTS_FROM_CONTRACT_LIST) return addLog({ error: 'No permission.' });

    if (!selectedContracts?.length) return addLog({ warning: 'Select at least one contract.' });

    if (hasMixedDealers) return addLog({ warning: 'Selected contracts must belong to the same dealer.' });

    const dealerId = selectedContracts[0].dealerId;
    const contractIds = selectedContracts.map((c) => c.id);

    const res = await createStatementsFromContractListApi({
      dealerId,
      createdAtLocal: date().format(defaultServerDateTimeFormat),
      contractIds,
    });

    if (res.status === HttpStatus.ok) {
      addLog({ success: 'Remittance created.' });

      listApi.execute((b) => b);
      setSelectedContracts([]);
    } else {
      addLog({ error: res.message ?? lang.error });
    }
  }
  //#endregion

  async function sendContractConsent({
    contract,
    consentRequestType,
  }: {
    contract: Model.IContract;
    consentRequestType: Model.Enum.ContractConsentRequestType;
  }) {
    const res = await contractSendConsentApi({ contractId: contract.id, consentRequestType });
    if (res.status === HttpStatus.ok)
      addLog({
        success:
          consentRequestType === ContractConsentRequestType.EMAIL
            ? lang.emailSuccessfullySent
            : lang.textSuccessfullySent,
      });
    else addLog({ error: lang.saveError });
  }

  //#region Refund pricing
  const refundConfigTemplateListApi = useApi(
    {
      action: RefundConfigTemplate.list,
      default: {
        isEnabled: true,
      },
      body: {
        productId: selected?.productId ? [selected.productId] : undefined,
      },
      wait: !selected?.productId,
    },
    [selected?.productId]
  );

  //#endregion
  return {
    canView,
    pageRouter,
    listApi,
    exportApi,
    selected,
    selectedProduct: productApi?.payload,
    permissions,
    hasAnyContractCommentPermission,
    revisionsApi,
    revisionUsersPayload,
    commentsApi,
    subfeeApi,
    subfees,
    subfeeNameMap,
    findApi,
    lender: lenderApi?.payload,
    userListApi,
    changeContractCreator,
    exportContractsToExcel,
    syncContractWithVertafore,
    syncContractDocumentWithVertafore,
    syncAllWithVertafore,
    regenerateContractDocuments,
    resendAllForSigning,
    reuploadAllSignedDocuments,
    sendContractConsent,
    selectedContracts,
    setSelectedContracts,
    getAllSelectedContracts,
    isFetchingEnvelopes,
    contractUnknownVertaforeStatusListApi,
    contractNotUploadedVertaforeStatusListApi,
    contractStatusAuditLogListApi,
    statuses,
    revisions,
    selectedRevision,
    selectRevision,
    subfeeDocs,
    isStatusChanging,
    setIsStatusChanging,
    hasMixedDealers,
    createStatementFromSelectedContracts,
    contractCancellation,
    refundConfigTemplateListApi,
    contractRefund,
    isContractEligibleForSelection,
  };
}

export function useSalesContractProvider() {
  return useDataProvider<ReturnType<typeof useSalesContract>>();
}

export function useContractComments() {
  const { lang } = getLocales();
  const { selected: contract, permissions, commentsApi, hasAnyContractCommentPermission } = useSalesContract();
  const auth = useAuthStore();
  const [action, setAction] = useState<'edit' | 'delete'>();
  const reloadComments = () => {
    commentsApi.execute((b) => b);
  };
  const form = useForm<Utils.FormData<Model.IContractComment>>({
    default: {
      contractId: contract?.id,
      userId: auth.userId,
      comment: undefined,
    },
    validation: (data, errors) => {
      if (!data.comment) errors.comment = lang.required;
    },
    onSubmit: async (data) => {
      const res = await contractCommentSaveApi({
        ...data,
        modifiedAtUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      });
      if (res.status !== HttpStatus.ok) addLog({ error: lang.unknownError });
      else {
        addLog({ success: lang.saveSuccess });
        reloadComments();
      }
    },
  });
  useEffect(() => {
    if (contract?.id) {
      form.update({ contractId: contract?.id });
    }
  }, [contract?.id]);
  useEffect(() => {
    if (form.isSubmitted) {
      setAction(null);
      form.reload();
    }
  }, [form.isSubmitted]);

  const deleteApi = useApi({
    action: contractCommentDeleteApi,
    callback: () => {
      setAction(null);
      form.reload();
      reloadComments();
    },
    wait: true,
  });
  return {
    form,
    deleteApi,
    isLoadingComments: commentsApi?.isExecuting,
    comments: commentsApi?.payload?.data ?? [],
    action,
    setAction,
    permissions,
    canDeleteComments: permissions?.DELETE_CONTRACTCOMMENT,
    canEditComments: permissions?.EDIT_CONTRACTCOMMENT,
    hasAnyContractCommentPermission,
    commentsApi,
  };
}
